'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Client-side form-token acquisition.
 *
 * WHY THIS EXISTS: form tokens carry a `renderedAt` stamp and expire after 24h
 * (see `formSigning.ts`). Minting one during SSR bakes an expiring value into
 * the HTML, which is fine for a per-request render and BROKEN the moment that
 * HTML is cached — every visitor shares one token, and once it ages out the
 * submit route rejects them as `invalid_token`, which is a SILENT drop. It also
 * defeats the per-render honeypot rotation, whose whole purpose is to stop
 * scrapers memorising the hidden-field name.
 *
 * So the token is fetched by the browser instead. The HTML stays a pure
 * function of (tenant, path) and is safe to cache for as long as we like.
 *
 * Fetched ON MOUNT, deliberately not on first interaction: the time-trap
 * compares submit time to the token's `renderedAt` and rejects anything faster
 * than `timeTrapMinSeconds` (default 2s). Minting at interaction time would
 * make a fast typist look like a bot and silently drop a real signup. Mount
 * keeps today's render→submit semantics.
 *
 * The request is memoised per hook instance, so a re-render can't stampede the
 * endpoint, and `ensureToken()` lets a submit handler await an in-flight fetch
 * rather than posting without a token.
 */

type FormTokenPair = {
  formToken: string
  honeypotKey: string
}

const DEFAULT_FORM_TOKEN_ENDPOINT = '/api/forms/token'

export type UseFormTokenResult = {
  /** Null until the fetch resolves (or forever, if it fails / is disabled). */
  pair: FormTokenPair | null
  /** Await the in-flight fetch — call from submit so we never post tokenless. */
  ensureToken: () => Promise<FormTokenPair | null>
}

export function useFormToken(opts: {
  formId: string
  /** Skip fetching — e.g. a caller that still supplies a server-minted token. */
  enabled?: boolean
  /** Defaults to `/api/forms/token`. */
  endpoint?: string
}): UseFormTokenResult {
  const { formId, enabled = true, endpoint = DEFAULT_FORM_TOKEN_ENDPOINT } = opts
  const [pair, setPair] = useState<FormTokenPair | null>(null)
  const inFlight = useRef<Promise<FormTokenPair | null> | null>(null)

  const ensureToken = useCallback((): Promise<FormTokenPair | null> => {
    if (!enabled || !formId) return Promise.resolve(null)
    if (!inFlight.current) {
      inFlight.current = fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formId }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((json: { formToken?: string; honeypotKey?: string } | null) => {
          if (!json?.formToken || !json.honeypotKey) return null
          const next = { formToken: json.formToken, honeypotKey: json.honeypotKey }
          setPair(next)
          return next
        })
        .catch(() => {
          // Let a later attempt (e.g. from submit) retry rather than pinning
          // the failure for the lifetime of the component.
          inFlight.current = null
          return null
        })
    }
    return inFlight.current
  }, [enabled, endpoint, formId])

  useEffect(() => {
    void ensureToken()
  }, [ensureToken])

  return { pair, ensureToken }
}
